export const SUPPORTED_LANGUAGES = [
    { code: "en", label: "English", dir: "ltr", locale: "en-PK" },
    { code: "ur", label: "اردو", dir: "rtl", locale: "ur-PK-u-nu-latn" },
    { code: "ar-AE", label: "العربية (الإمارات)", dir: "rtl", locale: "ar-AE-u-nu-latn" },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

export const LANGUAGE_CODES = ["en", "ur", "ar-AE"] as const satisfies readonly LanguageCode[];

export const DEFAULT_LANGUAGE: LanguageCode = "en";

export function isSupportedLanguage(value: unknown): value is LanguageCode {
    return SUPPORTED_LANGUAGES.some((l) => l.code === value);
}

/** Anything the server or browser hands us → a language we actually ship (else English). */
export function normalizeLanguage(value: unknown): LanguageCode {
    if (isSupportedLanguage(value)) return value;
    if (typeof value === "string") {
        const lower = value.toLowerCase();
        if (lower === "ar" || lower.startsWith("ar-")) return "ar-AE";
        if (lower === "ur" || lower.startsWith("ur-")) return "ur";
    }
    return DEFAULT_LANGUAGE;
}

export function directionOf(code: LanguageCode): "ltr" | "rtl" {
    return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.dir ?? "ltr";
}

export function isRtl(code: LanguageCode): boolean {
    return directionOf(code) === "rtl";
}

/** BCP-47 locale for Intl. Latin digits are forced so figures match the rest of the product. */
export function localeOf(code: LanguageCode): string {
    return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.locale ?? "en-PK";
}

export const CURRENCIES = ["PKR", "AED", "USD"] as const;
export type CurrencyCode = (typeof CURRENCIES)[number];
export const DEFAULT_CURRENCY: CurrencyCode = "PKR";

export function normalizeCurrency(value: unknown): CurrencyCode {
    return (CURRENCIES as readonly string[]).includes(value as string)
        ? (value as CurrencyCode)
        : DEFAULT_CURRENCY;
}
